import { InlineLink } from '@utilitywarehouse/hearth-react';

import { FAQLink } from '@components/FAQ';
import { EXTERNAL_LINKS } from '@constants/externalLinks';

import { FAQTopicRaw, Sections, Topics } from './types';

const SWITCHING_TOPICS: FAQTopicRaw[] = [
  {
    id: Topics.currentSupplierContact,
    question: 'Do I need to contact my current energy supplier?',
    defaultAnswer: (
      <>
        No. Once you decide to switch to UW, we'll contact your current supplier
        and take care of everything. We'll also keep you updated at every step
        of the way.
      </>
    ),
  },
  {
    id: Topics.switchPeriod,
    question: 'How long will my switch take?',
    defaultAnswer: (
      <>We'll get your switched over in as little as two working days.</>
    ),
  },
  {
    id: Topics.currentSupplierExitFees,
    question: 'Will you pay my exit fees to my current energy supplier?',
    defaultAnswer: (
      <>
        New customers who switch to UW Energy plus two additional UW services*
        are given up to £400 towards their early termination fees from their
        existing suppliers.
        <br />
        <br />
        *Eligible additional services are: Broadband, Mobile, Boiler & Home
        Cover, and Income Protector. Additional requirements apply to customers
        who are tenants.
      </>
    ),
  },
];

const PAYMENTS_TOPICS: FAQTopicRaw[] = [
  {
    id: Topics.creditCheck,
    question: 'Will I be credit checked?',
    defaultAnswer: 'No.',
    conditionalAnswer: (
      <>
        Like most utility companies, we’ll run a credit check to work out if
        you’ll need to pay us a deposit. This will leave a “footprint” on your
        credit file (see our{' '}
        <FAQLink href={EXTERNAL_LINKS.privacyPolicy}>Privacy Policy</FAQLink>)
      </>
    ),
    isPersonalized: true,
  },
  {
    id: Topics.depositQuestioning,
    question: 'Do you ask for a deposit?',
    defaultAnswer: 'No.',
    conditionalAnswer: (
      <>
        Like most utility companies, we’ll run a credit check to work out if
        you’ll need to pay us a deposit, and if so, how much it needs to be.
      </>
    ),
    isPersonalized: true,
  },
  {
    id: Topics.energyPayment,
    question: 'How do I pay for my energy?',
    defaultAnswer: (
      <>
        When you sign up online, we'll set up a monthly direct debit for you so
        your payments will be made automatically. If you'd rather pay a
        different way, you can give us a call on{' '}
        <InlineLink className="faqTextLink" href="tel:0333 777 0228">
          0333 777 0228
        </InlineLink>{' '}
        to sign up over the phone instead, or you can cancel your direct debit
        after you sign up online. You can then make your payments by phone, BACs
        transfer, cheque, postal order, or via your online account.
      </>
    ),
  },
  {
    id: Topics.whenWillTheMontlyBillBe,
    question: 'When will my monthly bill be sent?',
    defaultAnswer: (
      <>
        Typically, you’ll receive your bill monthly between the 7th and 25th of
        the month, depending on when you fall in our billing cycle. The quickest
        way to receive your bill is through our app and online account - these
        are normally available from the 8th of the month.
      </>
    ),
  },
  {
    id: Topics.directDebitComesOutDate,
    question: 'Can I choose which day of the month the direct debit comes out?',
    defaultAnswer: (
      <>
        We offer two Direct Debit collection dates – the first working day of
        the month or the last working day of the month.
        <br />
        <br />
        Unfortunately we’re unable to offer flexible payment dates. If you'd
        like to change your Direct Debit date, please contact us.
      </>
    ),
  },
];

const TARIFF_DETAILS_TOPICS: FAQTopicRaw[] = [
  {
    id: Topics.monthlyCostEstimate,
    question: 'Why is my monthly cost an estimate?',
    defaultAnswer: (
      <>
        The monthly amounts shown are based on the information you’ve given us.
        We take your expected energy usage for the next 12 months, work out the
        cost, and divide it by 12, so you’re spreading your energy costs over
        the whole year.
        <br />
        <br />
        After you’ve been with us for 3 months, we review your payment amount as
        part of an account health check, in case you're paying too much or too
        little. Hence the amount shown is a monthly estimate.
        <br />
        <br />
        Prepayment customers pay for their energy in advance by topping up their
        meter with credit.
      </>
    ),
  },
  {
    id: Topics.tariffChangesAfterCancellation,
    question:
      'What will happen to my energy tariff if I cancel one of my other services with UW?',
    defaultAnswer: (
      <>
        Your available energy tariffs are based on your chosen multi-service
        bundle. If you choose a variable tariff and cancel any non-energy
        services, you will be transferred to the cheapest available variable
        energy tariff.
        <br />
        <br />
        If you choose a fixed tariff and cancel any non-energy services, you
        will be transferred to the cheapest available fixed energy tariff of the
        same version number for the remainder of your fixed term.
      </>
    ),
  },
  // TODO: Decide what to do with this question
  // https://linear.app/utilitywarehouse/issue/AQ-3145/remove-renewable-from-all-our-estate
  // {
  //   id: Topics.renewableEnergy,
  //   question: 'Is the electricity and gas renewable?',
  //   defaultAnswer: (
  //     <>
  //       Customers who take our Double Gold tariff will benefit from 100%
  //       renewable electricity.
  //       <br />
  //       <br />
  //       The renewable electricity we sell for our Green tariffs is backed by
  //       Renewable Energy Guarantee of Origin certificates which allows Ofgem to
  //       verify the source of a supplier's electricity.
  //     </>
  //   ),
  // },
];

// https://docs.google.com/spreadsheets/d/1srXx8BuD0h_YIVOXX03kUHnzun7G5RXExTXOZXPW3fw/edit#gid=0
const faqSections = [
  {
    id: Sections.switching,
    title: 'Switching',
    topics: SWITCHING_TOPICS,
  },
  {
    id: Sections.payments,
    title: 'Payments',
    topics: PAYMENTS_TOPICS,
  },
  {
    id: Sections.tariffDetails,
    title: 'Tariff details',
    topics: TARIFF_DETAILS_TOPICS,
  },
];

export { faqSections };
